// src/pages/Admin/Articles/ArticlesAdmin.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import {
  getArticles,
  deleteArticle,
  activateArticle,
  updateOrderArticle,
  updateArticleImage,
} from "../../../services/articlesServices";
import {
  uploadArticlePhoto,
  toAbsoluteArticlePhotoUrl,
} from "../../../services/articlesPhotoServices";
import useScrollToTop from "../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import Button from "../../../components/Button/Button";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { RxDragHandleDots2 } from "react-icons/rx";
import { FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AddArticleModal from "./AddArticleModal";
import EditArticleModal from "./EditArticleModal";
import "react-toastify/dist/ReactToastify.css";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function ArticlesAdmin() {
  const FALLBACK = "/img/no-image.jpg";
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editableArticle, setEditableArticle] = useState(null);

  // Upload modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // "webp" | "jpg" | "png"

  // Filtres
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fStockable, setFStockable] = useState("");
  const [fActive, setFActive] = useState("");

  // Drag & Drop
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const hasActiveFilters =
    q.trim() !== "" || fCategory || fStockable || fActive;
  const canReorder = !hasActiveFilters && !loading;

  const normalizeOrder = (list) =>
    list.map((a, i) => ({ ...a, ordre_article: i + 1 }));

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getArticles();
      const sorted = (Array.isArray(data) ? data : []).sort(
        (a, b) => (a.ordre_article ?? 0) - (b.ordre_article ?? 0)
      );
      setArticles(sorted);
    } catch (error) {
      console.error("Erreur chargement des articles:", error);
      toast.error("Erreur lors du chargement des articles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Catégories pour le filtre
  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set(articles.map((a) => a.categorie).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b, "fr")),
    [articles]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return articles.filter((a) => {
      const matchQ =
        !query ||
        `${a.nom || ""} ${a.description || ""}`.toLowerCase().includes(query);
      const matchC = !fCategory || a.categorie === fCategory;
      const matchS =
        !fStockable ||
        (fStockable === "yes" ? a.is_stockable : !a.is_stockable);
      const matchA =
        !fActive || (fActive === "yes" ? a.is_active : !a.is_active);
      return matchQ && matchC && matchS && matchA;
    });
  }, [articles, q, fCategory, fStockable, fActive]);

  // ORDRE
  const persistOrder = async (reordered) => {
    try {
      await updateOrderArticle(
        reordered.map((a) => ({ id: a.id, ordre_article: a.ordre_article }))
      );
      await fetchArticles();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour de l’ordre.");
      fetchArticles();
    }
  };

  const handleDragStart = (e, id) => {
    if (!canReorder) return;
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id));
  };

  const handleDragOver = (e, id) => {
    if (!canReorder) return;
    e.preventDefault(); // nécessaire pour permettre le drop
    setDragOverId(id);
  };

  const handleDragLeave = () => {
    if (!canReorder) return;
    setDragOverId(null);
  };

  const handleDrop = async (e, targetId) => {
    if (!canReorder) return;
    e.preventDefault();
    const sourceIdStr = e.dataTransfer.getData("text/plain");
    const sourceId = sourceIdStr ? Number(sourceIdStr) : draggingId;
    if (!sourceId || sourceId === targetId) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }

    // Base triée par ordre actuel
    const base = [...articles].sort(
      (a, b) => (a.ordre_article ?? 0) - (b.ordre_article ?? 0)
    );
    const from = base.findIndex((a) => a.id === sourceId);
    const to = base.findIndex((a) => a.id === targetId);
    if (from === -1 || to === -1) {
      setDraggingId(null);
      setDragOverId(null);
      return;
    }
    const reordered = [...base];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    const withOrder = normalizeOrder(reordered);
    setArticles(withOrder);
    try {
      await persistOrder(withOrder);
      toast.success("Ordre mis à jour");
    } catch (_) {
      // persistOrder gère déjà l’erreur
    } finally {
      setDraggingId(null);
      setDragOverId(null);
    }
  };

  const moveArticleUp = async (index) => {
    if (index === 0) return;
    const reordered = [...articles];
    const temp = reordered[index - 1];
    reordered[index - 1] = reordered[index];
    reordered[index] = temp;
    // Ajuste les ordres affichés (optionnel si le back recalcule)
    reordered[index - 1].ordre_article--;
    reordered[index].ordre_article++;
    setArticles(reordered);
    await persistOrder(reordered);
  };

  const moveArticleDown = async (index) => {
    if (index === articles.length - 1) return;
    const reordered = [...articles];
    const temp = reordered[index + 1];
    reordered[index + 1] = reordered[index];
    reordered[index] = temp;
    reordered[index + 1].ordre_article++;
    reordered[index].ordre_article--;
    setArticles(reordered);
    await persistOrder(reordered);
  };

  // CRUD
  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await deleteArticle(id);
      toast.success("Article supprimé avec succès");
      fetchArticles();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression de l'article");
    }
  };

  const toggleActiveStatus = async (id) => {
    try {
      await activateArticle(id);
      toast.success("Statut de l'article mis à jour");
      fetchArticles();
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  // Upload image
  const openUploadModal = (article) => {
    setEditableArticle(article);
    setSelectedFile(null);
    setSelectedPreview(null);
    setIsUploadModalOpen(true);
  };
  const closeUploadModal = () => {
    setEditableArticle(null);
    setSelectedFile(null);
    setSelectedPreview(null);
    setIsUploadModalOpen(false);
  };
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    setSelectedPreview(file ? URL.createObjectURL(file) : null);
  };
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const updated = await uploadArticlePhoto(
        editableArticle.id,
        selectedFile,
        {
          format: outputFormat,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          },
        }
      );
      const absUrl = toAbsoluteArticlePhotoUrl(updated?.photo_url);
      setArticles((prev) =>
        prev.map((a) =>
          a.id === editableArticle.id ? { ...a, photo_url: absUrl } : a
        )
      );
      toast.success("Image mise à jour !");
      closeUploadModal();
    } catch (error) {
      console.error("Erreur upload image :", error);
      toast.error("Erreur lors de l'upload de l'image.");
    } finally {
      setIsUploading(false);
      fetchArticles();
    }
  };

  return (
    <section className="w-full">
      {/* Offset pour la navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Administrateur"
          title="Articles Boutique & Licence-Cotisation"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Header dégradé + instructions + bouton d’ajout */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white space-y-2">
              <h3 className="text-lg sm:text-xl font-semibold text-center">
                Gérez votre catalogue d’articles boutique &
                Licence-Cotisation-Réduction
              </h3>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center">
                Ajoutez vos articles, <span>activez/désactivez</span> leur
                visibilité en boutique, ajustez le <span>stock</span> et
                organisez l’<span>ordre d’affichage</span>.
              </p>
              <div className="mt-4 flex justify-center">
                <Button
                  icon={FaPlus}
                  text="Ajouter un article"
                  onClick={() => setIsAddModalOpen(true)}
                  className="!rounded-xl !shadow-md !bg-white !text-slate-900 hover:!bg-white/90 gap-2"
                />
              </div>
            </div>
          </div>

          {/* Toolbar filtres */}
          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="Rechercher (nom, description)…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
              <select
                value={fCategory}
                onChange={(e) => setFCategory(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Toutes catégories</option>
                {categoryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={fStockable}
                onChange={(e) => setFStockable(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">Stockable (tous)</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
              <select
                value={fActive}
                onChange={(e) => setFActive(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="">En boutique (tous)</option>
                <option value="yes">Affiché</option>
                <option value="no">Masqué</option>
              </select>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {!loading && (
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                {canReorder
                  ? "Astuce : maintenez et faites glisser une ligne (ou une carte sur mobile) pour réordonner."
                  : "Désactivez la recherche/les filtres pour réordonner par glisser-déposer."}
              </p>
            )}
            {/* Skeleton simple */}
            {loading && (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun article
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Ajoutez un article ou modifiez vos filtres.
                </p>
              </div>
            )}

            {/* Table desktop */}
            {!loading && filtered.length > 0 && (
              <div className="hidden md:block">
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {[
                          "Ordre",
                          "Article",
                          "Prix",
                          "Taille",
                          "Stockable",
                          "Stock",
                          "Catégorie",
                          "Boutique",
                          "Actions",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
                      {filtered.map((article, index) => (
                        <tr
                          key={article.id}
                          draggable={canReorder}
                          onDragStart={(e) => handleDragStart(e, article.id)}
                          onDragOver={(e) => handleDragOver(e, article.id)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, article.id)}
                          aria-grabbed={draggingId === article.id}
                          className={`group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40 ${
                            canReorder
                              ? "cursor-grab active:cursor-grabbing"
                              : ""
                          } ${
                            dragOverId === article.id
                              ? "ring-2 ring-violet-400"
                              : ""
                          }`}
                        >
                          {/* Ordre */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                title={
                                  canReorder ? "Glisser pour réordonner" : ""
                                }
                                className={`text-slate-400 ${
                                  canReorder
                                    ? "cursor-grab active:cursor-grabbing"
                                    : ""
                                }`}
                              >
                                <RxDragHandleDots2 />
                              </span>
                              <span className="inline-block min-w-6 text-center font-semibold">
                                {article.ordre_article}
                              </span>
                              <div className="flex flex-col -my-1">
                                <button
                                  onClick={() =>
                                    moveArticleUp(
                                      articles.findIndex(
                                        (a) => a.id === article.id
                                      )
                                    )
                                  }
                                  title="Monter"
                                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
                                >
                                  <TbTriangleFilled />
                                </button>
                                <button
                                  onClick={() =>
                                    moveArticleDown(
                                      articles.findIndex(
                                        (a) => a.id === article.id
                                      )
                                    )
                                  }
                                  title="Descendre"
                                  className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
                                >
                                  <TbTriangleInvertedFilled />
                                </button>
                              </div>
                            </div>
                          </td>

                          {/* Article (photo + infos) */}
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-3">
                              <div className="relative">
                                {article.photo_url ? (
                                  <img
                                    src={
                                      toAbsoluteArticlePhotoUrl(
                                        article.photo_url
                                      ) || FALLBACK
                                    }
                                    alt={article.nom}
                                    className="h-16 w-16 rounded-lg object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                                    onError={(e) =>
                                      (e.currentTarget.src = FALLBACK)
                                    }
                                  />
                                ) : (
                                  <div className="h-16 w-16 rounded-lg bg-slate-100 dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700" />
                                )}
                                <button
                                  type="button"
                                  onClick={() => openUploadModal(article)}
                                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] bg-white/90 dark:bg-slate-900/90 rounded px-1 py-0.5 ring-1 ring-slate-200 dark:ring-slate-700 hover:bg-white"
                                >
                                  image
                                </button>
                              </div>
                              <div>
                                <div className="font-semibold text-slate-900 dark:text-slate-100">
                                  {article.nom}
                                </div>
                                <div className="text-slate-600 dark:text-slate-300 line-clamp-2 max-w-[520px]">
                                  {article.description}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3 font-semibold">
                            {article.prix}€
                          </td>
                          <td className="px-4 py-3">{article.taille || "—"}</td>

                          <td className="px-4 py-3">
                            {article.is_stockable ? "✅" : "❌"}
                          </td>

                          <td className="px-4 py-3">
                            {article.quantite_stock ?? 0}
                          </td>

                          <td className="px-4 py-3">
                            {article.categorie || "—"}
                          </td>

                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleActiveStatus(article.id)}
                              className="rounded-md px-2 py-0.5 text-xs font-semibold text-white bg-slate-500 hover:opacity-90"
                              title="Basculer en boutique"
                              aria-label="Basculer en boutique"
                              style={{
                                background: article.is_active
                                  ? "#10b981"
                                  : "#9ca3af",
                              }}
                            >
                              {article.is_active ? "Affiché" : "Masqué"}
                            </button>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <ButtonEdit
                                onClick={() => {
                                  setEditableArticle(article);
                                  setIsEditModalOpen(true);
                                }}
                              />
                              <ButtonDelete
                                onClick={() => handleDeleteArticle(article.id)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Cartes mobile */}
            {!loading && filtered.length > 0 && (
              <div className="md:hidden space-y-3">
                {filtered.map((article) => (
                  <div
                    key={article.id}
                    draggable={canReorder}
                    onDragStart={(e) => handleDragStart(e, article.id)}
                    onDragOver={(e) => handleDragOver(e, article.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, article.id)}
                    className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 shadow-sm hover:shadow-md overflow-hidden ${
                      canReorder ? "cursor-grab active:cursor-grabbing" : ""
                    } ${
                      dragOverId === article.id ? "ring-2 ring-violet-400" : ""
                    }`}
                  >
                    <div className="flex">
                      {/* Visuel : 1/3 largeur, pleine hauteur */}
                      <div className="relative w-1/3 h-28">
                        {article.photo_url ? (
                          <img
                            src={
                              toAbsoluteArticlePhotoUrl(article.photo_url) ||
                              FALLBACK
                            }
                            alt={article.nom}
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800" />
                        )}
                        <button
                          type="button"
                          onClick={() => openUploadModal(article)}
                          className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] bg-white/90 dark:bg-slate-900/90 rounded px-1.5 py-0.5 ring-1 ring-slate-200 dark:ring-slate-700"
                        >
                          image
                        </button>
                      </div>

                      {/* Contenu : 2/3 largeur */}
                      <div className="w-2/3 p-3 flex flex-col">
                        <div className="flex items-start gap-2">
                          <span className="mt-[2px] text-slate-400">
                            <RxDragHandleDots2 />
                          </span>
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {article.nom}
                          </h3>
                          <span className="ml-auto text-sm font-bold">
                            {article.prix}€
                          </span>
                        </div>

                        {article.description ? (
                          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                            {article.description}
                          </p>
                        ) : null}

                        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                          <div>Taille : {article.taille || "—"}</div>
                          <div>Catégorie : {article.categorie || "—"}</div>
                          <div>
                            Stockable : {article.is_stockable ? "✅" : "❌"}
                          </div>
                          <div>Stock : {article.quantite_stock ?? 0}</div>
                        </div>

                        <div className="mt-auto pt-2 flex items-center gap-2">
                          <button
                            onClick={() => toggleActiveStatus(article.id)}
                            className={`rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${
                              article.is_active
                                ? "bg-emerald-600"
                                : "bg-slate-400"
                            }`}
                          >
                            {article.is_active ? "Actif" : "Inactif"}
                          </button>

                          <div className="ml-auto flex items-center gap-1">
                            <button
                              onClick={() =>
                                moveArticleUp(
                                  articles.findIndex((a) => a.id === article.id)
                                )
                              }
                              className="p-1 text-slate-500 hover:text-slate-900 dark:hover:text-white"
                              title="Monter"
                              aria-label="Monter"
                            >
                              <TbTriangleFilled />
                            </button>
                            <button
                              onClick={() =>
                                moveArticleDown(
                                  articles.findIndex((a) => a.id === article.id)
                                )
                              }
                              className="p-1 text-slate-500 hover:text-slate-900 dark:hover:text-white"
                              title="Descendre"
                              aria-label="Descendre"
                            >
                              <TbTriangleInvertedFilled />
                            </button>

                            <ButtonEdit
                              onClick={() => {
                                setEditableArticle(article);
                                setIsEditModalOpen(true);
                              }}
                            />
                            <ButtonDelete
                              onClick={() => handleDeleteArticle(article.id)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <ToastContainer />
      </div>

      {/* Modale Upload Image */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* overlay */}
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={closeUploadModal}
          />
          <div className="relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
              <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
                Changer l’image{" "}
                {editableArticle ? `: ${editableArticle.nom}` : ""}
              </h3>
            </div>

            <form
              onSubmit={handleUploadSubmit}
              className="p-5 sm:p-6 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Fichier image
                  </label>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    Formats courants supportés (jpeg, png, webp…)
                  </p>

                  {/* Choix du format de sortie */}
                  <div className="mt-3 flex items-center gap-3">
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      Format
                    </span>
                    <select
                      className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-sm"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      disabled={isUploading}
                    >
                      <option value="webp">WEBP (recommandé)</option>
                      <option value="jpg">JPG</option>
                      <option value="png">PNG</option>
                    </select>
                  </div>

                  {/* Progression d’upload */}
                  {isUploading && (
                    <div className="mt-3">
                      <progress
                        className="progress w-full"
                        value={uploadProgress}
                        max="100"
                      />
                      <div className="text-xs mt-1">{uploadProgress}%</div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Aperçu
                  </label>
                  <div className="h-28 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                    {selectedPreview ? (
                      <img
                        src={selectedPreview}
                        alt="Preview"
                        className="h-full object-contain"
                      />
                    ) : editableArticle?.photo_url ? (
                      <img
                        src={toAbsoluteArticlePhotoUrl(
                          editableArticle.photo_url
                        )}
                        alt={editableArticle.nom}
                        className="h-full object-contain"
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    ) : (
                      <span className="text-xs text-slate-500">
                        Aucun aperçu
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  text="Fermer"
                  icon={FaTimes}
                  onClick={closeUploadModal}
                  type="button"
                  className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
                />
                <Button
                  text={isUploading ? "Envoi…" : "Sauvegarder"}
                  icon={FaSave}
                  type="submit"
                  disabled={!selectedFile || isUploading}
                  className={`!rounded-xl gap-2 ${
                    !selectedFile || isUploading
                      ? "opacity-60 pointer-events-none"
                      : ""
                  }`}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modales CRUD */}
      <AddArticleModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        refreshList={fetchArticles}
      />
      <EditArticleModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        article={editableArticle}
        refreshList={fetchArticles}
      />
    </section>
  );
}
